import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getOrgPolicyV1, getPolicyV1ById, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import FormSection from '@/components/form/form-section';
import useQueryHelper from '@/hooks/use-query-helper';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormButtons from '@/components/form/form-buttons';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PortalDeprecatedToast from '@/components/portal-deprecated-toast';
import PageFeatureToast from '@/components/page-feature-toast';
import { getAllAgents } from '@/lib/services/agent.service';
import { Box, Grid, Stack } from '@mui/material';
import InlineToast from '@/components/toasts/inline';
import Switch from '@/components/form/switch';
import { createUseStyles } from 'react-jss';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import ModelOption from '@/components/model-option';
import { cloneDeep } from 'lodash';
import Chip from '@/components/chip';
import { FieldError } from '@/lib/services';
import FormLabel from '@/components/form-label';
import AgentLink from '@/components/agent-link';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['contextDataSources', 'assistants', 'portal'];
const formModel = new PolicyModelV1();

interface Props {
  policyId: string;
}

const useStyles = createUseStyles({
  portalSelect: {
    '& .MuiFormControl-root': {
      marginTop: 0,
    },
  },
});

const PortalForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canChangeAgents, hasPortalChat } = useFeature();
  const isPortalChatDisabled = !hasPortalChat;
  const styles = useStyles();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      if (orgPolicy) {
        updateValues.stripOrgPolicy(orgPolicy);
      }
      const { patched, error, fieldErrors } = await patchPolicyV1(
        policyId,
        updateValues.propSlice(fields)
      );

      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const query = useQuery([QueryKey.PolicyCompView, policyId], async () => {
    const [policy, orgPolicy] = await Promise.all([getPolicyV1ById(policyId), getOrgPolicyV1()]);

    if (policy && orgPolicy) {
      policy.applyOrgPolicy(orgPolicy);
    }

    return [policy, orgPolicy];
  });
  const { data } = query;
  const [, orgPolicy] = (data || [null, null]) as [PolicyModelV1 | null, PolicyModelV1 | null];

  const privateModelsQuery = useQuery([QueryKey.PrivateModelsList], async () =>
    getAllPrivateModels()
  );
  const { data: privateModels } = privateModelsQuery;

  const agentsQuery = useQuery([QueryKey.AgentsList], async () => getAllAgents());
  const { data: agents } = agentsQuery;

  const { values } = formHandle;

  const availablePrivateModelOptions = useMemo(() => {
    const orgEnabledModels = orgPolicy?.portal?.availablePrivateModelIds || [];

    return (privateModels || []).map(({ name, id, modelId }) => {
      const orgEnabled = orgEnabledModels.includes(id);
      const isDefault = id === values.portal.defaultModelId;

      let tooltip = '';
      if (orgEnabled) {
        tooltip = 'This model is enabled by default policy and cannot be disabled by group policy';
      } else if (isDefault) {
        tooltip = 'The default model must be enabled';
      }

      return {
        label: <ModelOption name={name} modelId={modelId} />,
        value: id,
        disabled: orgEnabled || isDefault,
        tooltip,
      };
    });
  }, [privateModels, orgPolicy, values.portal.defaultModelId]);

  const defaultPrivateModelOptions = useMemo(() => {
    const orgDefaultModel = orgPolicy?.portal?.defaultModelId;

    return (privateModels || []).map(({ name, id, modelId }) => {
      const isOrgDefault = orgDefaultModel === id;

      const compName = isOrgDefault ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>{name}</Box>
          <Chip label="Default Policy" size="x-small" />
        </Stack>
      ) : (
        name
      );

      return {
        label: <ModelOption name={compName} modelId={modelId} />,
        value: id,
        disabled: !values.portal.availablePrivateModelIds.includes(id),
      };
    });
  }, [privateModels, orgPolicy, values.portal.availablePrivateModelIds]);

  const agentOptions = useMemo(() => {
    return (agents || []).map((agent) => ({
      value: agent.id,
      label: <AgentLink agent={agent} showAdminName />,
    }));
  }, [agents]);

  const { showLoader: agentsLoading } = useQueryHelper(agentsQuery);
  const { showLoader: privateModelsLoading } = useQueryHelper(privateModelsQuery);

  if (agentsLoading || privateModelsLoading) {
    return;
  }

  const hasPrivateModel = !!availablePrivateModelOptions.length;
  const canChangePortalEnabled = !orgPolicy?.portal?.enabled;

  return (
    <Form formHandle={formHandle} query={query}>
      <PortalDeprecatedToast />
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection
        title="Allow Access to Portal Chat"
        scrollId="portal-access"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>
          Enable or disable Portal chat access for groups using this policy.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {canChangePortalEnabled && (
              <InputControl
                label="Portal Enabled"
                name="portal.enabled"
                type="switch"
                formHandle={formHandle}
                disabled={isPortalChatDisabled}
                readonly={!canChangePolicy}
              />
            )}

            {!canChangePortalEnabled && (
              <Stack direction="row" gap={2} alignItems="center">
                <Box>
                  <Switch
                    label="Portal Chat Enabled"
                    name="canChangePortalEnabled"
                    checked
                    disabled
                    onChange={() => null}
                  />
                </Box>
                <InlineToast
                  level="warning"
                  message="The Portal is enabled by the Default Policy, and therefore cannot be disabled by group policy"
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Portal Private Model Settings"
        scrollId="portal-private-model-settings"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>
          Choose which private models are available, and the default, when a user is redirected to
          Portal Chat.
        </FormSectionSubtitle>
        <Grid container columnSpacing={8} rowSpacing={4}>
          <Grid item xs={6}>
            <Box>
              <FormLabel>Enabled Private Models</FormLabel>
              <InputControl
                name="portal.availablePrivateModelIds"
                label=""
                type="switch-list"
                options={availablePrivateModelOptions}
                formHandle={formHandle}
                disabled={!hasPrivateModel || isPortalChatDisabled}
                readonly={!canChangePolicy}
                emptyMessage="No private models available"
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <FormLabel>Default Private Model</FormLabel>
            <Box className={styles.portalSelect}>
              <InputControl
                label=""
                name="portal.defaultModelId"
                type="select"
                disabled={!hasPrivateModel || isPortalChatDisabled}
                options={defaultPrivateModelOptions}
                formHandle={formHandle}
                readonly={!canChangePolicy}
              />
            </Box>

            {!privateModelsLoading && !hasPrivateModel && (
              <Box mt={2.5}>
                <InlineToast
                  level="warning"
                  message="Enable a Private Model to enable this feature"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Data Source Assignment"
        scrollId="data-source-assignment"
        disabled={isPortalChatDisabled}
      >
        <InputControl
          name="contextDataSources.enabled"
          formHandle={formHandle}
          disabled={isPortalChatDisabled}
          readonly={!canChangePolicy}
          type="datasources-list-toggle"
        />
      </FormSection>

      <FormSection
        title="Assistant Assignment"
        scrollId="assistant-assignment"
        disabled={isPortalChatDisabled}
      >
        <InputControl
          name="assistants.enabled"
          label="Assistants"
          formHandle={formHandle}
          disabled={isPortalChatDisabled}
          readonly={!canChangeAgents}
          type="list-toggle"
          leftTitle="Available Assistants"
          rightTitle="Assigned Assistants"
          options={agentOptions}
        />
      </FormSection>

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy || isPortalChatDisabled} />
    </Form>
  );
};

export default PortalForm;
